import React, { useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  Grid,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { HighlightOff } from '@mui/icons-material';
import { validateSearchName } from '../utils/savedSearchHelpers';
import { VISIBILITY_TYPES } from '../utils/savedSearchConstants';
import EnhancedTextField from '../../EnhancedTextField';
import { DialogHeader } from '../../../theme';
import { SavedSearch } from '../types';

interface RenameDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (newName: string) => Promise<void>;
  savedSearch: SavedSearch | null;
  existingNames: SavedSearch[];
}

const WarningText = styled('div')(({ theme }) => ({
  color: '#d32f2f',
  fontSize: '14px',
  marginBottom: '12px',
  padding: '8px',
  backgroundColor: 'rgba(211, 47, 47, 0.05)',
  borderRadius: '4px',
  border: '1px solid rgba(211, 47, 47, 0.2)'
}));

function RenameDialog({ open, onClose, onConfirm, savedSearch, existingNames }: RenameDialogProps) {
  const [newName, setNewName] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (open) {
      setNewName(savedSearch?.name || '');
      setError('');
    }
  }, [open, savedSearch]);

  const handleConfirm = async () => {
    const validationError = validateSearchName(newName, existingNames);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    await onConfirm(newName);
    setSaving(false);

    handleClose();
  };

  const handleClose = (..._args: any[]) => {
    if (!saving) {
      setNewName('');
      setError('');
      onClose();
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewName(e.target.value);
    if (error) {
      setError('');
    }
  };

  const isShared = savedSearch?.visibility === VISIBILITY_TYPES.COMPANY ||
                   savedSearch?.visibility === VISIBILITY_TYPES.PARTNERS;

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          if (saving) return;
        }
        handleClose(event, reason);
      }}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          style: {
            borderRadius: "12px"
          }
        }
      }}>
      <DialogTitle style={{ paddingBottom: '8px' }}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item container style={{ flex: 0.5, flexDirection: "column" }}>
            <DialogHeader>
              Rename Saved Search
            </DialogHeader>
          </Grid>

          <IconButton
            onClick={handleClose}
            disabled={saving}
            style={{ padding: '8px' }}
            size="large">
            <HighlightOff style={{ color: "red" }}/>
          </IconButton>
        </Grid>
      </DialogTitle>
      <DialogContent style={{ paddingTop: '8px', paddingBottom: '16px' }}>
        {isShared && (
          <WarningText>
            <strong>Warning:</strong> This is a shared view. Changes made to shared views will apply to all users in your company.
          </WarningText>
        )}

        <Typography style={{ marginBottom: '12px', fontSize: '14px', color: '#666' }}>
          Are you sure you want to rename this view from <strong>"{savedSearch?.name}"</strong> to:
        </Typography>

        <EnhancedTextField
          autoFocus
          placeholder="Enter new name"
          value={newName}
          onChange={handleNameChange}
          error={error}
          disabled={saving}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && !error && newName.trim() && newName !== savedSearch?.name) {
              handleConfirm();
            }
          }}
        />
      </DialogContent>
      <DialogActions style={{ padding: "16px", paddingTop: "8px" }}>
        <Button disabled={saving} onClick={handleClose}>Cancel</Button>
        <Button color="primary" disabled={!newName.trim() || newName === savedSearch?.name || saving} onClick={handleConfirm}>Continue</Button>
      </DialogActions>
    </Dialog>
  );
}

export default RenameDialog;
